import React, { useState, useEffect } from "react";
import { Paper, Chip, Tooltip, Box, InputAdornment } from "@mui/material";
import {
  Group,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Warning,
  Search,
  Star,
  StarBorder,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDInput from "shared/components/MDInput";
import DataTable from "shared/components/Tables/DataTable";

import { getVehicleStatus } from "../../model/vipCameModel";

// Función para obtener el tipo de parqueadero basado en el estado
const getParkingTypeFromStatus = (status) => {
  if (status === "EN VIP") return "vip";
  if (status === "EN GENERAL") return "general";
  return null;
};

const GroupsTable = ({ groups, allVehicles = [], loading = false, occupancyKnown = true }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);

  // Construir mapa de vehículos por grupo con datos actualizados
  const getVehiclesByGroup = () => {
    const vehiclesMap = {};

    if (Array.isArray(allVehicles) && allVehicles.length > 0) {
      allVehicles.forEach((vehicle) => {
        const groupId = vehicle.idgroup;
        if (!vehiclesMap[groupId]) {
          vehiclesMap[groupId] = [];
        }
        vehiclesMap[groupId].push(vehicle);
      });
    }

    return vehiclesMap;
  };

  // Calcular estadísticas por grupo usando la nueva lógica de estado
  const getGroupStats = (groupId, vehiclesMap) => {
    const groupVehicles = vehiclesMap[groupId] || [];
    const totalVehicles = groupVehicles.length;

    // Usar getVehicleStatus para contar correctamente
    const vehiclesInside = groupVehicles.filter((v) => {
      const status = getVehicleStatus(v);
      return status === "EN VIP" || status === "EN GENERAL" || status === "DENTRO";
    }).length;

    const vehiclesOutside = groupVehicles.filter((v) => getVehicleStatus(v) === "FUERA").length;

    // Contar VIP y General basado en el estado real
    const vipCount = groupVehicles.filter((v) => getVehicleStatus(v) === "EN VIP").length;
    const generalCount = groupVehicles.filter((v) => getVehicleStatus(v) === "EN GENERAL").length;

    // Vehículos que están dentro pero no tienen zona definida (solo "DENTRO")
    const undefinedZoneCount = groupVehicles.filter((v) => getVehicleStatus(v) === "DENTRO").length;

    return {
      totalVehicles,
      vehiclesInside,
      vehiclesOutside,
      vipCount,
      generalCount,
      undefinedZoneCount,
    };
  };

  // Filtrar grupos por búsqueda
  useEffect(() => {
    if (!Array.isArray(groups)) {
      setFilteredGroups([]);
      return;
    }

    if (!searchQuery.trim()) {
      setFilteredGroups(groups);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = groups.filter(
        (group) =>
          group.name?.toLowerCase().includes(query) || group.idgroup?.toString().includes(query)
      );
      setFilteredGroups(filtered);
    }
  }, [searchQuery, groups]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  // Determinar color según uso de cupos
  const getQuotaStatusColor = (used, total) => {
    if (total === 0) return "default";
    const percentage = (used / total) * 100;
    if (percentage >= 100) return "error";
    if (percentage >= 80) return "warning";
    if (percentage >= 50) return "info";
    return "success";
  };

  // Determinar ícono según uso de cupos
  const getQuotaIcon = (used, total) => {
    if (total === 0) return <TrendingFlat fontSize="small" />;
    const percentage = (used / total) * 100;
    if (percentage >= 100) return <TrendingDown sx={{ color: "#f44336" }} />;
    if (percentage >= 80) return <TrendingDown sx={{ color: "#ff9800" }} />;
    if (percentage >= 50) return <TrendingFlat sx={{ color: "#2196f3" }} />;
    return <TrendingUp sx={{ color: "#4caf50" }} />;
  };

  // Columnas de la tabla con tamaños ajustados
  const columns = [
    { Header: "Grupo", accessor: "groupName", width: "20%" },
    { Header: "Cupos", accessor: "quotas", width: "6%" },
    { Header: "Uso de Cupos", accessor: "quotaUsage", width: "16%" },
    { Header: "Total Vehículos", accessor: "totalVehicles", width: "8%" },
    { Header: "En Parqueadero", accessor: "vehiclesInside", width: "10%" },
    { Header: "Fuera", accessor: "vehiclesOutside", width: "8%" },
    { Header: "VIP / General", accessor: "vipGeneral", width: "18%" },
    { Header: "Distribución VIP/General", accessor: "distribution", width: "16%" },
  ];

  // Preparar filas - usar grupos filtrados
  const vehiclesMap = getVehiclesByGroup();

  const rows = filteredGroups.map((group) => {
    const stats = getGroupStats(group.idgroup, vehiclesMap);
    const quotas = group.quotas || 0;
    const usage = quotas > 0 ? Math.round((stats.vehiclesInside / quotas) * 100) : 0;
    const isFull = stats.vehiclesInside >= quotas && quotas > 0;
    const isNearFull = usage >= 80 && usage < 100 && quotas > 0;

    // Calcular porcentajes para la barra de distribución VIP/General
    const vipPercentage =
      stats.vehiclesInside > 0 ? Math.round((stats.vipCount / stats.vehiclesInside) * 100) : 0;
    const generalPercentage =
      stats.vehiclesInside > 0 ? Math.round((stats.generalCount / stats.vehiclesInside) * 100) : 0;

    return {
      groupName: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <Group sx={{ color: "#4361ee", fontSize: "1rem" }} />
          <Box>
            <MDTypography
              variant="body2"
              fontWeight="bold"
              color="text.primary"
              sx={{ fontSize: "0.9rem" }}
            >
              {group.name}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              ID: {group.idgroup}
            </MDTypography>
          </Box>
        </MDBox>
      ),
      quotas: (
        <MDBox textAlign="center">
          <MDTypography variant="body2" fontWeight="bold" color="text.primary">
            {quotas}
          </MDTypography>
        </MDBox>
      ),
      quotaUsage: (
        <MDBox>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <MDTypography
              variant="caption"
              fontWeight="medium"
              color="text.secondary"
              sx={{ fontSize: "0.7rem" }}
            >
              {usage}%
            </MDTypography>
            {getQuotaIcon(stats.vehiclesInside, quotas)}
          </MDBox>
          <Box
            sx={{
              width: "100%",
              height: "4px",
              backgroundColor: "#e0e0e0",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${Math.min(100, usage)}%`,
                height: "100%",
                backgroundColor: isFull
                  ? "#f44336"
                  : isNearFull
                  ? "#ff9800"
                  : getQuotaStatusColor(stats.vehiclesInside, quotas) === "info"
                  ? "#2196f3"
                  : "#4caf50",
                transition: "width 0.3s ease",
              }}
            />
          </Box>
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block", fontSize: "0.65rem" }}
          >
            {Math.max(0, quotas - stats.vehiclesInside)} libres de {quotas}
          </MDTypography>
        </MDBox>
      ),
      totalVehicles: (
        <MDBox textAlign="center">
          <MDTypography variant="body2" fontWeight="bold" color="text.primary">
            {stats.totalVehicles}
          </MDTypography>
        </MDBox>
      ),
      vehiclesInside: (
        <MDBox textAlign="center">
          <Chip
            label={stats.vehiclesInside}
            size="small"
            color={isFull ? "error" : stats.vehiclesInside > 0 ? "success" : "default"}
            sx={{
              fontWeight: "bold",
              fontSize: "0.75rem",
              minWidth: "40px",
              height: "24px",
            }}
          />
          {isFull && (
            <Tooltip title="Cupos agotados" arrow>
              <Warning
                sx={{ fontSize: "0.75rem", color: "#f44336", ml: 0.5, verticalAlign: "middle" }}
              />
            </Tooltip>
          )}
          {isNearFull && !isFull && (
            <Tooltip title="Cupos casi llenos" arrow>
              <Warning
                sx={{ fontSize: "0.75rem", color: "#ff9800", ml: 0.5, verticalAlign: "middle" }}
              />
            </Tooltip>
          )}
        </MDBox>
      ),
      vehiclesOutside: (
        <MDBox textAlign="center">
          <Chip
            label={stats.vehiclesOutside}
            size="small"
            color="default"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              fontSize: "0.75rem",
              minWidth: "40px",
              height: "24px",
            }}
          />
        </MDBox>
      ),
      vipGeneral: (
        <MDBox textAlign="center">
          <MDBox display="flex" justifyContent="center" gap={0.5} flexWrap="wrap">
            <Tooltip title={`${stats.vipCount} vehículos en zona VIP`} arrow>
              <Chip
                icon={<Star sx={{ fontSize: "0.75rem !important", color: "#ffc107" }} />}
                label={stats.vipCount}
                size="small"
                color="warning"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  height: "24px",
                  "& .MuiChip-icon": { marginLeft: "4px", marginRight: "-4px" },
                }}
              />
            </Tooltip>
            <Tooltip title={`${stats.generalCount} vehículos en zona General`} arrow>
              <Chip
                icon={<StarBorder sx={{ fontSize: "0.75rem !important" }} />}
                label={stats.generalCount}
                size="small"
                color="info"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  height: "24px",
                  "& .MuiChip-icon": { marginLeft: "4px", marginRight: "-4px" },
                }}
              />
            </Tooltip>
            {stats.undefinedZoneCount > 0 && (
              <Tooltip
                title={`${stats.undefinedZoneCount} vehículos dentro sin zona definida`}
                arrow
              >
                <Chip
                  label={`? ${stats.undefinedZoneCount}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    height: "24px",
                    borderColor: "#9e9e9e",
                    color: "#9e9e9e",
                  }}
                />
              </Tooltip>
            )}
          </MDBox>
        </MDBox>
      ),
      distribution: (
        <MDBox>
          {stats.vehiclesInside > 0 ? (
            <>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <Star sx={{ fontSize: "0.7rem", color: "#ffc107" }} />
                  <MDTypography variant="caption" fontWeight="medium" sx={{ fontSize: "0.65rem" }}>
                    {vipPercentage}%
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <StarBorder sx={{ fontSize: "0.7rem", color: "#2196f3" }} />
                  <MDTypography variant="caption" fontWeight="medium" sx={{ fontSize: "0.65rem" }}>
                    {generalPercentage}%
                  </MDTypography>
                </MDBox>
              </MDBox>
              <Box
                sx={{
                  width: "100%",
                  height: "6px",
                  backgroundColor: "#e0e0e0",
                  borderRadius: "3px",
                  overflow: "hidden",
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: `${vipPercentage}%`,
                    height: "100%",
                    backgroundColor: "#ffc107",
                    transition: "width 0.3s ease",
                  }}
                />
                <Box
                  sx={{
                    width: `${generalPercentage}%`,
                    height: "100%",
                    backgroundColor: "#2196f3",
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block", fontSize: "0.6rem", textAlign: "center" }}
              >
                {stats.vipCount} VIP · {stats.generalCount} General
                {stats.undefinedZoneCount > 0 && ` · ${stats.undefinedZoneCount} sin zona`}
              </MDTypography>
            </>
          ) : (
            <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              Sin vehículos dentro
            </MDTypography>
          )}
        </MDBox>
      ),
    };
  });

  if (!occupancyKnown)
    rows.forEach((row) => {
      ["quotaUsage", "vehiclesInside", "vehiclesOutside", "vipGeneral", "distribution"].forEach(
        (key) => {
          row[key] = (
            <MDTypography variant="caption" color="text">
              Sin información
            </MDTypography>
          );
        }
      );
    });

  if (loading) {
    return (
      <MDBox p={3} textAlign="center">
        <MDTypography variant="body2" color="text.secondary">
          Cargando datos de grupos...
        </MDTypography>
      </MDBox>
    );
  }

  if (rows.length === 0) {
    return (
      <MDBox p={3} textAlign="center">
        <MDTypography variant="body2" color="text.secondary">
          {searchQuery ? "No se encontraron grupos" : "No hay grupos disponibles"}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Barra de búsqueda */}
      <MDBox p={2} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <MDInput
          fullWidth
          placeholder="Buscar grupo por nombre o ID..."
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#9e9e9e", fontSize: "1rem" }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <Box
                  component="span"
                  onClick={clearSearch}
                  sx={{ cursor: "pointer", fontSize: "1rem", color: "#9e9e9e" }}
                >
                  ✕
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
              py: 1,
            },
          }}
        />
      </MDBox>

      <DataTable
        table={{ columns, rows }}
        isSorted={false}
        entriesPerPage={10}
        showTotalEntries={true}
        noEndBorder={true}
        canSearch={false}
        pagination={{ variant: "gradient", color: "primary" }}
      />
    </Paper>
  );
};

GroupsTable.propTypes = {
  groups: PropTypes.array.isRequired,
  allVehicles: PropTypes.array,
  loading: PropTypes.bool,
  occupancyKnown: PropTypes.bool,
};

GroupsTable.defaultProps = {
  allVehicles: [],
  loading: false,
};

export default GroupsTable;
